import { apiClient } from './apiClient';
import {
  UISettingsEffective,
  UISettingsListResponse,
  UISettingsPayload,
  UISettingsScope,
} from '../types/uiSettings';
import { safeStorage } from '../utils/safeStorage';
import { decodeJwtPayload } from '../utils/jwt';

// Treat the stored access token as usable only if it's present AND its `exp`
// claim is in the future. Skipping the request when the token is stale avoids
// the 401-then-refresh-then-retry round-trip that spams the browser console
// with "Failed to load resource: 401" lines during context bootstrap.
function hasUsableAccessToken(): boolean {
  if (typeof window === 'undefined') return false;
  const token = safeStorage.getItem('access_token');
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;  // can't tell — let the request go
  return payload.exp * 1000 > Date.now();
}

class UISettingsService {
  // Any authenticated user. Contexts call this once at boot to fill blanks.
  // Tolerates 401s (returns null) so the contexts can fall through to built-ins.
  async getEffective(): Promise<UISettingsEffective | null> {
    try {
      return (await apiClient.get('/v2/ui-settings/effective')) as UISettingsEffective;
    } catch {
      return null;
    }
  }

  // SuperAdmin only — used by the SettingsPage editor.
  async listAll(): Promise<UISettingsListResponse> {
    return (await apiClient.get('/v2/ui-settings')) as UISettingsListResponse;
  }

  async upsert(
    scopeType: string,
    scopeValue: string,
    payload: UISettingsPayload
  ): Promise<UISettingsScope> {
    const encoded = `${encodeURIComponent(scopeType)}/${encodeURIComponent(scopeValue)}`;
    return (await apiClient.put(`/v2/ui-settings/${encoded}`, payload)) as UISettingsScope;
  }

  async remove(scopeType: string, scopeValue: string): Promise<void> {
    const encoded = `${encodeURIComponent(scopeType)}/${encodeURIComponent(scopeValue)}`;
    await apiClient.delete(`/v2/ui-settings/${encoded}`);
  }
}

export const uiSettingsService = new UISettingsService();

// Shared cached promise so ThemeContext / I18nContext / UiVisibilityContext
// collapse their three mount-time fetches into one network call. The cache
// is invalidated on auth changes (login/logout) so the next consumer gets
// fresh data instead of the previous user's defaults.
let effectivePromise: Promise<UISettingsEffective | null> | null = null;

export function fetchEffectiveOnce(): Promise<UISettingsEffective | null> {
  // Skip the request when the access token is missing or already expired.
  // Hitting the endpoint with no/expired token returns 401 → apiClient
  // interceptor redirects to login → would loop. Fail fast to null and let
  // contexts retry on `app:auth-ready` after refresh.
  if (!hasUsableAccessToken()) {
    return Promise.resolve(null);
  }
  if (!effectivePromise) {
    effectivePromise = uiSettingsService.getEffective();
  }
  return effectivePromise;
}

export function invalidateEffective(): void {
  effectivePromise = null;
}
